// ARCA-599 (ARCA-590) — single source of truth for anchor-placement eligibility.
//
// The T2/T3 anchor gate offers tappable targets from TWO surfaces that must
// agree exactly: the Pixi AnchorPlacementOverlay (the visible Sun-Yellow
// borders) and the DOM MatchGrid cell <button> (which actually intercepts the
// tap/keyboard activation because it sits above the Pixi layer in the stacking
// order). Centralising the predicate here keeps the two surfaces from drifting.
//
// ARCA-749 (ARCA-717 WS3) — anchors now place on the WORD field, a grid fully
// decoupled from the (separate, fixed 6×6) chess board. A word cell carries NO
// piece columns, so the ARCA-599 anchor-on-piece exclusion is MOOT: a piece on
// chess cell N and an anchor on word cell N are on different surfaces and cannot
// collide. The piece check is therefore dropped — anchors place freely on any
// lettered, non-anchor, non-Hollow word cell. (The backend's
// `validateAnchorPlacement` dropped its matching `cell_has_piece` rule in WS1.)

import type { WordFieldCell } from './wire-grids'

/** A single word-field cell — the anchor gate's grid (no piece columns). */
export type BoardCell = WordFieldCell

/**
 * The minimal cell surface the predicate reads. Typed structurally (not as the
 * full {@link WordFieldCell}) so BOTH the word-field cells the overlay filters
 * AND the combined `board` cells the DOM MatchGrid indexes — whose `is_frozen`
 * is optional — satisfy it without a cast. The predicate never reads a piece
 * column, so the chess surface is irrelevant here by construction.
 */
interface AnchorCandidateCell {
  is_anchor: boolean
  is_hollow: boolean
  letter: string | null
}

/**
 * Whether `cell` is a legal anchor-placement target for the UI to offer.
 *
 * Mirrors the (WS1-updated) backend validator `validateAnchorPlacement`
 * (apps/backend/src/match/anchors/placement.ts), which rejects a placement only
 * when the word cell is already an anchor or is a Hollow.
 *
 * Frozen *empty* cells stay eligible — the backend does NOT reject them, so the
 * UI must not either (ARCA-590).
 *
 * The `letter !== null` clause is a UI-SPEC §8 ergonomic narrowing (anchor
 * targets are only offered on lettered runes). It is stricter than the backend,
 * never looser, so it can only hide a legal-but-letterless cell, never offer an
 * illegal one.
 */
export function isEligibleAnchorCell(cell: AnchorCandidateCell): boolean {
  return (
    cell.is_anchor === false && cell.is_hollow === false && cell.letter !== null
  )
}
